import React, { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useApiClient } from '../../api/ApiClientContext';
import { Icon } from '../../assets/icons/Icon';
import layoutStyles from '../../styles/sidebarLayout.module.scss';
import styles from './ProfilePage.module.scss';
import HabitTrackingHistory from '../../components/habitHistory/HabitTrackingHistory';

type ProfileTab = 'profile' | 'history';

function resizeImageToJpeg(file: File, size = 320): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('Canvas not supported'));
        return;
      }
      const cropSize = Math.min(img.width, img.height);
      const sx = (img.width - cropSize) / 2;
      const sy = (img.height - cropSize) / 2;
      ctx.drawImage(img, sx, sy, cropSize, cropSize, 0, 0, size, size);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (blob) resolve(blob);
          else reject(new Error('Failed to create image blob'));
        },
        'image/jpeg',
        0.9,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}

const ProfilePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tabParam = searchParams.get('tab');
  const activeTab: ProfileTab = tabParam === 'history' ? 'history' : 'profile';
  const [avatarError, setAvatarError] = useState(false);

  const setActiveTab = (tab: ProfileTab) => {
    navigate(`/profile?tab=${tab}`, { replace: true });
  };
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const { data: profileData } = useQuery({
    queryKey: ['currentUserProfile'],
    queryFn: async () => {
      const res = await apiClient.api.getCurrentUserProfileApiV1AuthMeGet();
      return res?.data;
    },
  });

  const avatarUrl = profileData?.profile?.avatar_url;
  const displayName = profileData
    ? `${profileData.user.first_name} ${profileData.user.last_name}`
    : '';
  const displayEmail = profileData?.user.email ?? '';
  const displayJobTitle = profileData?.user.job_title;

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const uploadAvatarMutation = useMutation({
    mutationFn: async (file: File) => {
      const resizedBlob = await resizeImageToJpeg(file);
      const resizedFile = new File([resizedBlob], 'avatar.jpg', { type: 'image/jpeg' });
      return apiClient.api.uploadAvatarApiV1AuthAvatarPost({ file: resizedFile });
    },
    onSuccess: () => {
      setUploadError(null);
      setAvatarError(false);
      queryClient.invalidateQueries({ queryKey: ['currentUserProfile'] });
    },
    onError: (error: any) => {
      const message =
        error?.error?.detail || error?.message || 'Failed to upload avatar. Please try again.';
      setUploadError(message);
    },
  });

  const deleteAvatarMutation = useMutation({
    mutationFn: async () => {
      return apiClient.api.deleteAvatarApiV1AuthAvatarDelete();
    },
    onSuccess: () => {
      setUploadError(null);
      setAvatarError(false);
      queryClient.invalidateQueries({ queryKey: ['currentUserProfile'] });
    },
    onError: (error: any) => {
      const message =
        error?.error?.detail || error?.message || 'Failed to remove avatar. Please try again.';
      setUploadError(message);
    },
  });

  const handleEditClick = () => {
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      event.target.value = '';

      const validTypes = ['image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        setUploadError('Only JPEG and PNG images are accepted');
        return;
      }

      const maxSize = 3 * 1024 * 1024;
      if (file.size > maxSize) {
        setUploadError('File size must not exceed 3MB');
        return;
      }

      uploadAvatarMutation.mutate(file);
    },
    [uploadAvatarMutation],
  );

  const handleDeleteAvatar = () => {
    deleteAvatarMutation.mutate();
  };

  const showAvatarImage = avatarUrl && !avatarError;
  const isUploading = uploadAvatarMutation.isPending;
  const isDeleting = deleteAvatarMutation.isPending;

  return (
    <div className={`${layoutStyles.page} ${styles.pageOverride}`}>
      <div className={layoutStyles.pageContainer}>
        {/* Sidebar */}
        <aside className={layoutStyles.sidebar}>
          <nav className={layoutStyles.sidebarNav}>
            <button
              className={`${layoutStyles.sidebarTab} ${activeTab === 'profile' ? layoutStyles.active : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              Profile
            </button>
            <button
              className={`${layoutStyles.sidebarTab} ${activeTab === 'history' ? layoutStyles.active : ''}`}
              onClick={() => setActiveTab('history')}
            >
              Habit tracking history
            </button>
          </nav>
        </aside>

        {/* Main content */}
        <main className={layoutStyles.mainContent}>
          {activeTab === 'profile' && (
            <>
              <h1 className={layoutStyles.pageTitle}>Profile</h1>
              <div className={layoutStyles.tabContent}>
                <div className={styles.profileCard}>
                  <div className={styles.userSection}>
                    {/* Avatar + Edit */}
                    <div className={styles.avatarColumn}>
                      <div className={styles.avatarWrapper}>
                        {showAvatarImage ? (
                          <img
                            src={avatarUrl}
                            alt={displayName}
                            className={styles.avatarImage}
                            onError={() => setAvatarError(true)}
                          />
                        ) : (
                          <div className={styles.avatarFallback}>
                            <span className={styles.avatarText}>
                              {displayName ? getInitials(displayName) : ''}
                            </span>
                          </div>
                        )}
                        {isUploading && <div className={styles.avatarOverlay}>Uploading...</div>}
                      </div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept="image/jpeg,image/png"
                        className={styles.hiddenInput}
                      />
                      <div className={styles.avatarActions}>
                        <button
                          className={styles.editButton}
                          onClick={handleEditClick}
                          disabled={isUploading}
                        >
                          Edit
                        </button>
                        {avatarUrl && !avatarError && (
                          <button
                            className={styles.removeButton}
                            onClick={handleDeleteAvatar}
                            disabled={isDeleting}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>

                    {/* User Info + Email */}
                    <div className={styles.infoColumn}>
                      <div className={styles.userInfo}>
                        <h2 className={styles.userName}>{displayName}</h2>
                        {displayEmail && (
                          <div className={styles.userDetail}>
                            <Icon iconName="AtsignIcon" width="0.75rem" height="0.75rem" />
                            <span>{displayEmail}</span>
                          </div>
                        )}
                        {displayJobTitle && (
                          <div className={styles.userDetail}>
                            <Icon iconName="UsersIcon" width="0.75rem" height="0.75rem" />
                            <span>{displayJobTitle}</span>
                          </div>
                        )}
                      </div>

                      <div className={styles.formSection}>
                        <label className={styles.fieldLabel}>Email</label>
                        <input
                          type="email"
                          className={styles.fieldInput}
                          value={displayEmail}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  {uploadError && (
                    <div className={styles.errorMessage}>
                      <span>{uploadError}</span>
                      <button
                        className={styles.errorDismiss}
                        onClick={() => setUploadError(null)}
                        aria-label="Dismiss error"
                      >
                        &times;
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {activeTab === 'history' && (
            <>
              <h1 className={layoutStyles.pageTitle}>Habit tracking history</h1>
              <div className={layoutStyles.tabContent}>
                <HabitTrackingHistory />
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default ProfilePage;
